import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PropertyCard from '../components/PropertyCard';
import SearchFilters from '../components/SearchFilters';
import { FiSearch } from 'react-icons/fi';
import './PropertyList.css';

const PropertyList = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    current_page: 1
  });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchProperties();
  }, [searchParams, filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Check if this is a location-based search
      const location = searchParams.get('location');
      const lat = searchParams.get('lat');
      const lng = searchParams.get('lng');
      const radius = searchParams.get('radius') || 5;

      let endpoint = '/api/properties';
      let isLocationSearch = false;

      // Use nearby endpoint if location or coordinates are provided
      if (location || (lat && lng)) {
        endpoint = '/api/properties/nearby';
        isLocationSearch = true;

        if (location) {
          params.append('location', location);
        } else {
          params.append('lat', lat);
          params.append('lng', lng);
        }
        params.append('radius', radius);
      } else {
        // Add search query for regular search
        const q = searchParams.get('q');
        if (q) params.append('q', q);

        // Add filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      }

      // Add pagination
      const page = searchParams.get('page') || 1;
      params.append('page', page);
      params.append('per_page', 12);

      const response = await api.get(`${endpoint}?${params.toString()}`);
      setProperties(response.data.properties);
      setPagination({
        total: response.data.total,
        pages: response.data.pages,
        current_page: response.data.current_page
      });
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery, page: 1 });
    } else {
      setSearchParams({});
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSearchParams({ ...Object.fromEntries(searchParams), page: 1 });
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ ...Object.fromEntries(searchParams), page: newPage });
    window.scrollTo(0, 0);
  };

  return (
    <div className="property-list-page">
      <div className="container-wide">
        {/* Search Bar */}
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('property.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input-large"
            />
            <button type="submit" className="btn btn-primary">
              {t('property.search')}
            </button>
          </form>
          <SearchFilters onFilterChange={handleFilterChange} initialFilters={filters} />
        </div>

        {/* Results */}
        <div className="results-header">
          <h2 className="results-title">
            {searchParams.get('location')
              ? `Propriedades próximas a ${searchParams.get('location')}`
              : searchParams.get('q')
              ? `${t('property.searchResults')} "${searchParams.get('q')}"`
              : t('property.allProperties')}
          </h2>
          <p className="results-count">
            {pagination.total} {t('property.propertiesFound')}
            {searchParams.get('location') && ` (raio de ${searchParams.get('radius') || 5}km)`}
          </p>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : properties.length === 0 ? (
          <div className="no-results">
            <h3>{t('property.noProperties')}</h3>
            <p>{t('property.tryAdjusting')}</p>
          </div>
        ) : (
          <>
            {/* Property Grid */}
            <div className="grid grid-4">
              {properties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  showHost
                  showDistance={!!searchParams.get('location')}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="btn btn-secondary"
                >
                  {t('property.previous')}
                </button>

                <div className="pagination-info">
                  {t('property.page')} {pagination.current_page} {t('property.of')} {pagination.pages}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.pages}
                  className="btn btn-secondary"
                >
                  {t('property.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyList;
